import { Input, MyDatePicker, PhotoEditModal, UserAvatar } from '@/components'
import colors from '@/styles/colors'
import { ChevronLeft, Close } from '@mui/icons-material'
import SVG from 'react-inlinesvg'
import { Box, Button, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material'

import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { SVGS } from '@/assets/svgs'
import dayjs, { Dayjs } from 'dayjs'
import { MyBasicInfoContainer } from '../__styled'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Patterns } from '@/utils/helper'
import {
  useUpdateProfile,
  useGetBasicInfo,
  useUploadProfilePicture,
  useUploadCoverPicture,
} from '@/hooks/api/dashboard'
import { toast } from 'react-hot-toast'
import { useAuth, useLocalStorage } from '@/hooks/common'
import { CBox, LoadingButtonRounded, RBox } from '@/pages/__styled'
import { RoundedActionButton } from '@/components/StyledComponents'
import { dateOfBirthProps, MDDatePicker } from '@/components/DatePickerWithHelpText'

enum PickerMode {
  AVATAR,
  COVER,
}

interface FormDataRow {
  icon: string
  title: string
  value: string
}

interface ProfileForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  dob: Dayjs
  profession: string
  motto: string
}

export default function MyBasicInfo() {
  const { logout } = useAuth()
  const [editing, setEditing] = useState(false)
  const [showAvatarModal, setShowAvatarModal] = useState(false)
  const imagePickerMode = useRef<PickerMode>(PickerMode.AVATAR)

  const [profile] = useAppStore((state) => [state.user.profile], shallow)
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileForm>({
    defaultValues: {
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      email: profile?.userEmail,
      phone: profile?.phoneNumber,
      dob: dayjs(profile?.dob, 'MMMM D, YYYY'),
      profession: profile?.profession,
      motto: profile?.motto,
    },
  })

  const [ewUser] = useLocalStorage('ewUser', null)
  const updateProfile = useUpdateProfile()
  const getProfileData = useGetBasicInfo(ewUser?.userID)
  const uploadAvatar = useUploadProfilePicture()
  const uploadCoverPicture = useUploadCoverPicture()
  const avatarRef = useRef<any>(null)

  useEffect(() => {
    if (updateProfile.isSuccess || uploadAvatar.isSuccess || uploadCoverPicture.isSuccess) {
      setEditing(false)
      setShowAvatarModal(false)
      getProfileData.mutate({ isUserProfile: false })
    }
  }, [updateProfile.isSuccess, uploadAvatar.isSuccess, uploadCoverPicture.isSuccess])

  const dataRows = useMemo(() => {
    let result: FormDataRow[] = []

    if (profile) {
      result.push({
        icon: SVGS.EmailIcon,
        title: 'Email',
        value: profile?.userEmail || '',
      })

      result.push({
        icon: SVGS.Phone,
        title: 'Phone Number',
        value: profile?.phoneNumber || '',
      })

      result.push({
        icon: SVGS.CalendarIconThin,
        title: 'Date Of Birth',
        value: profile?.dob || '',
      })

      result.push({
        icon: SVGS.ClockIcon,
        title: 'Member since',
        value: `${dayjs(profile?.joinDate).format('MMM YYYY')}`,
      })
    }
    return result
  }, [profile])

  const onSubmit = (data: ProfileForm) => {
    toast.promise(
      updateProfile.mutateAsync({
        day: dayjs(data.dob).date().toString(),
        month: (dayjs(data.dob).month() + 1).toString(),
        year: dayjs(data.dob).year().toString(),
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        motto: data.motto,
        profession: data.profession,
        phone: data.phone,
      }),
      {
        loading: 'Updating your profile...',
        success: <Typography variant='p'>Profile has been updated.</Typography>,
        error: <Typography variant='p'>Failed to update</Typography>,
      },
    )
  }

  const onClickLogout = () => {
    logout()
  }

  const pickAvatarPhoto = () => {
    imagePickerMode.current = PickerMode.AVATAR
    setShowAvatarModal(true)
  }
  const pickCoverPhoto = () => {
    imagePickerMode.current = PickerMode.COVER
    setShowAvatarModal(true)
  }

  const onSelectPhoto = () => {
    if (imagePickerMode.current == PickerMode.AVATAR) {
      onSaveAvatar()
    } else {
      onSaveCover()
    }
  }

  const onSaveAvatar = () => {
    if (uploadAvatar.isLoading) return
    if (!avatarRef.current) return
    const url = avatarRef.current.getImageScaledToCanvas().toDataURL()
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `profilePicture.png`, {
          type: 'image/png',
        })
        toast.promise(uploadAvatar.mutateAsync({ file }), {
          loading: 'Updating your avatar...',
          success: <Typography variant='p'>Avatar has been updated.</Typography>,
          error: <Typography variant='p'>Failed to update</Typography>,
        })
      })
  }

  const onSaveCover = () => {
    if (uploadCoverPicture.isLoading) return
    if (!avatarRef.current) return
    const url = avatarRef.current.getImageScaledToCanvas().toDataURL()
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `profilePicture.png`, {
          type: 'image/png',
        })
        toast.promise(uploadCoverPicture.mutateAsync({ file }), {
          loading: 'Updating your cover photo...',
          success: <Typography variant='p'>Cover has been updated.</Typography>,
          error: <Typography variant='p'>Failed to update</Typography>,
        })
      })
  }

  if (editing) {
    return (
      <MyBasicInfoContainer py={2}>
        <Box className={'closeButton'}>
          <IconButton
            onClick={() => setEditing(false)}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: '#f8fafc',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              borderRadius: '12px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: '#ffffff',
                borderColor: 'rgba(99, 102, 241, 0.3)',
                transform: 'rotate(90deg)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
              },
              '&:active': {
                transform: 'rotate(90deg) scale(0.95)',
              },
            }}
          >
            <Close sx={{ color: '#64748b', fontSize: 20 }} />
          </IconButton>
        </Box>
        <Box display={'flex'} flexDirection='column' alignItems='center' mb={4} mt={3}>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#0f172a',
              letterSpacing: '-0.02em',
              mb: 3,
            }}
          >
            Edit Profile
          </Typography>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <Box position={'relative'}>
              <UserAvatar
                name={`${profile?.firstName} ${profile?.lastName}`}
                url={profile?.profilePicURL}
                borderRadius={100}
                size={100}
                style={{
                  border: '4px white solid',
                  boxShadow: '0 8px 24px rgba(99, 102, 241, 0.15)',
                }}
              />
              <IconButton
                onClick={pickAvatarPhoto}
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  border: `3px solid white`,
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <SVG src={SVGS.Camera} color='white' width='18' height='18' />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <CBox className='formFields' gap={2.5} px={'28px'} pb={3}>
          <Controller
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                name='first-name'
                label='First Name'
                defaultValue=''
                variant='outlined'
                onChange={field.onChange}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                size='medium'
              />
            )}
            name='firstName'
            rules={{ required: 'First name is required' }}
          />
          <Controller
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                name='last-name'
                label='Last Name'
                defaultValue=''
                variant='outlined'
                onChange={field.onChange}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                size='medium'
              />
            )}
            name='lastName'
            rules={{ required: 'Last name is required' }}
          />
          <Controller
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                name='email-address'
                label='Email'
                defaultValue=''
                variant='outlined'
                onChange={field.onChange}
                error={!!errors.email}
                helperText={errors.email?.message}
                size='medium'
              />
            )}
            name='email'
            rules={{
              required: 'Email address is required',
              pattern: { value: Patterns.email, message: 'Email address is invalid' },
            }}
          />
          <Controller
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                name='phone-number'
                label='Phone Number'
                defaultValue=''
                variant='outlined'
                onChange={field.onChange}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                size='medium'
              />
            )}
            name='phone'
            rules={{
              required: 'Phone number is required',
            }}
          />
          {/* <Typography variant='h3' fontSize={12} mt={2} mb={1}>
            Date of Birth
          </Typography> */}
          <Controller
            control={control}
            render={({ field }) => (
              <MDDatePicker
                value={field.value}
                errorText={errors.dob?.message}
                isError={!!errors.dob}
                onChange={field.onChange}
                {...dateOfBirthProps}
              />
            )}
            name='dob'
            rules={{
              required: 'Date of Birth is required',
              validate: (value: Dayjs) => dayjs(value).isValid() || 'Invalid date',
            }}
          />
          <Controller
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                name='text'
                label='Profession'
                defaultValue=''
                variant='outlined'
                onChange={field.onChange}
                size='medium'
              />
            )}
            name='profession'
          />
          <Controller
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                name='text'
                label='Motto'
                defaultValue=''
                variant='outlined'
                onChange={field.onChange}
              />
            )}
            name='motto'
          />

          <Button
            onClick={handleSubmit(onSubmit)}
            fullWidth
            disabled={!isDirty || updateProfile.isLoading}
            sx={{
              height: 48,
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              textTransform: 'none',
              background: !isDirty
                ? '#f8fafc'
                : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: !isDirty ? '#cbd5e1' : '#ffffff',
              border: `2px solid ${!isDirty ? 'rgba(226, 232, 240, 0.8)' : 'transparent'}`,
              boxShadow: !isDirty ? 'none' : '0 4px 16px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: !isDirty
                  ? '#f8fafc'
                  : 'linear-gradient(135deg, #5558e3 0%, #7c48e8 100%)',
                transform: !isDirty ? 'none' : 'translateY(-2px)',
                boxShadow: !isDirty ? 'none' : '0 6px 24px rgba(99, 102, 241, 0.4)',
              },
              '&:active': {
                transform: !isDirty ? 'none' : 'translateY(0) scale(0.98)',
              },
              '&.Mui-disabled': {
                color: '#cbd5e1',
                backgroundColor: '#f8fafc',
                borderColor: 'rgba(226, 232, 240, 0.8)',
              },
            }}
          >
            {updateProfile.isLoading ? 'Updating...' : 'Update Profile'}
          </Button>
        </CBox>

        <PhotoEditModal
          editorRef={avatarRef}
          title={
            imagePickerMode.current == PickerMode.AVATAR
              ? 'Add your avatar'
              : 'Add your cover photo'
          }
          open={showAvatarModal}
          onClose={() => setShowAvatarModal(false)}
          onSubmit={onSelectPhoto}
        />
      </MyBasicInfoContainer>
    )
  }

  return (
    <MyBasicInfoContainer>
      <Box
        className={'cover'}
        onClick={pickCoverPhoto}
        sx={{
          backgroundImage: profile?.profileCoverURL
            ? `linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%), url(${profile?.profileCoverURL})`
            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
        }}
      >
        <IconButton className={'cover-camera'} onClick={pickCoverPhoto}>
          <SVG src={SVGS.Camera} width={'18px'} height={'18px'} color='white' />
        </IconButton>
      </Box>

      <CBox sx={{ pb: '32px', px: '24px', zIndex: 10, mt: '100px' }}>
        <Box display={'flex'} flexDirection='column'>
          <Box
            sx={{
              position: 'relative',
              '&::before': {
                display: 'none',
              },
            }}
          >
            <UserAvatar
              name={`${profile?.firstName} ${profile?.lastName}`}
              url={profile?.profilePicURL}
              borderRadius={100}
              size={110}
              style={{
                border: '5px white solid',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.2)',
              }}
            />
          </Box>
          <RBox sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mt: 2.5 }}>
            <CBox gap={'6px'}>
              <Typography
                fontSize={'24px'}
                fontWeight={'700'}
                letterSpacing={'-0.02em'}
                lineHeight={1.2}
                sx={{
                  color: '#0f172a',
                }}
              >{`${profile?.firstName} ${profile?.lastName}`}</Typography>
              <Typography
                variant='p'
                fontSize={'15px'}
                fontWeight={500}
                sx={{
                  color: '#64748b',
                }}
              >
                {profile?.profession || 'No profession set'}
              </Typography>
            </CBox>
            <IconButton
              onClick={() => setEditing(true)}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                borderRadius: '12px',
                width: '44px',
                height: '44px',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                },
                '&:active': {
                  transform: 'translateY(0) scale(0.95)',
                },
              }}
            >
              <SVG src={SVGS.PencilBoxIcon} width={'20px'} height={'20px'} color='white' />
            </IconButton>
          </RBox>

          {profile?.motto && profile?.motto?.length > 0 && (
            <Box
              mt={'20px'}
              sx={{
                position: 'relative',
                backgroundColor: 'rgba(99, 102, 241, 0.04)',
                padding: '16px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  borderRadius: '12px 0 0 12px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                },
              }}
            >
              <Typography
                fontSize={'14px'}
                fontWeight={500}
                lineHeight={1.6}
                sx={{
                  color: '#475569',
                  fontStyle: 'italic',
                }}
              >
                "{profile?.motto}"
              </Typography>
            </Box>
          )}
        </Box>

        {/* Separator */}
        <Box
          sx={{
            width: '100%',
            height: '1px',
            my: '32px',
            background:
              'linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.8) 50%, transparent 100%)',
          }}
        />

        <Grid container spacing={'16px'}>
          {dataRows.map((e, i) => (
            <Grid item xs={12} sm={12} lg={12} key={`${i}`}>
              <Box
                display='flex'
                alignItems='center'
                sx={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    borderColor: 'rgba(99, 102, 241, 0.2)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background:
                      'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.1)',
                  }}
                >
                  <SVG src={e.icon} color='#6366f1' width={'20px'} height={'20px'} />
                </Box>
                <CBox ml={'16px'} gap={'4px'}>
                  <Typography
                    fontSize={'11px'}
                    fontWeight={600}
                    sx={{
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {e.title}
                  </Typography>
                  <Typography
                    fontSize={'14px'}
                    fontWeight={600}
                    sx={{
                      color: '#0f172a',
                    }}
                  >
                    {e.value}
                  </Typography>
                </CBox>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box display='flex' flexDirection={'column'} justifyContent={'center'} mt={'32px'} gap={1}>
          <Button
            onClick={onClickLogout}
            sx={{
              height: '44px',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              textTransform: 'none',
              backgroundColor: '#f8fafc',
              color: '#ef4444',
              border: '2px solid rgba(239, 68, 68, 0.2)',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.08)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: '#ef4444',
                color: '#ffffff',
                borderColor: '#ef4444',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(239, 68, 68, 0.25)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.95)',
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </CBox>
      <PhotoEditModal
        editorRef={avatarRef}
        title={
          imagePickerMode.current == PickerMode.AVATAR ? 'Add your avatar' : 'Add your cover photo'
        }
        open={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onSubmit={onSelectPhoto}
      />
    </MyBasicInfoContainer>
  )
}
